import { Check, AlertCircle } from 'lucide-react'
import { useState } from 'react'

import { PhoneFieldInput } from '@/components/forms/PhoneFieldInput'
import { FileFieldInput } from '@/features/records/components/FileFieldInput'
import { FieldHelpText, LayoutFieldView } from '@/features/records/components/LayoutFieldView'
import { RecordFieldInput } from '@/features/records/components/RecordFieldInput'
import { isButtonField } from '@/features/records/utils/buttonField'
import { isDataField } from '@/features/records/utils/fieldAccess'
import { isFieldVisible, valuesByKey } from '@/features/records/utils/fieldConditions'
import { splitIntoSteps } from '@/features/records/utils/formSteps'
import { fieldWidth, isLayoutField } from '@/features/records/utils/layoutField'
import { defaultValues, isHiddenOnPublicForm, prefillValues } from '@/features/records/utils/prefill'
import type { FileUploadConfig, UploadedFile } from '@/types/api/file'
import type { FormField } from '@/types/api/form'
import { FormFieldType } from '@/types/enums'
import { cn } from '@/utils/cn'
import { validateFieldValue as validateSharedField, getFieldInputType } from '@/utils/field-formatters'

import { FieldInput, SelectFieldInput, ChoiceFieldInput, BooleanFieldInput } from './FormFieldInputs'

interface FormPreviewProps {
  fields: FormField[]
  workspaceName?: string
  title?: string | null
  description?: string | null
  /** If true, renders the "submitter info" header section (name + email). */
  showSubmitterFields?: boolean
  /** Called when mock-submit fires and all validation passes. Pass undefined to suppress. */
  onMockSubmit?: (values: Record<string, string>, submitterName: string, submitterEmail: string) => void
  /** Show a success state instead of the form. */
  success?: boolean
  successMessage?: string | null
  /**
   * Uploads a FILE_UPLOAD field value. When omitted (form designer preview) file
   * fields render as read-only placeholders.
   */
  onFileUpload?: (
    field: FormField,
    file: File,
    options: { onProgress: (percent: number) => void; signal: AbortSignal }
  ) => Promise<UploadedFile>
  /** Global upload policy, required for client-side size and type validation. */
  fileUploadConfig?: Pick<FileUploadConfig, 'maxFileSizeBytes' | 'allowedTypes'> | null
  /** Answers from the link's query string, by field key. */
  prefill?: Record<string, string>
}

function getInputType(type: FormFieldType) {
  return getFieldInputType(type)
}

function validateField(value: string, field: FormField): string | null {
  const v = field.validations as Record<string, unknown> | null ?? {}
  const trimmed = value.trim()

  const shared = validateSharedField(value, field.type, field.required, v, field.options)
  if (shared) return shared

  if (field.required && !trimmed) return 'This field is required'
  if (!trimmed) return null

  switch (field.type) {
    case FormFieldType.EMAIL: {
      const re = (v.pattern as string) ? new RegExp(v.pattern as string) : /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!re.test(trimmed)) return 'Invalid email format'
      break
    }
    case FormFieldType.NUMBER:
    case FormFieldType.CURRENCY: {
      const n = parseFloat(trimmed)
      if (isNaN(n)) return 'Must be a valid number'
      if (v.min != null && n < (v.min as number)) return `Must be >= ${v.min}`
      if (v.max != null && n > (v.max as number)) return `Must be <= ${v.max}`
      break
    }
    case FormFieldType.TEXT: {
      if (v.minLength != null && trimmed.length < (v.minLength as number))
        return `Min ${v.minLength} characters`
      if (v.maxLength != null && trimmed.length > (v.maxLength as number))
        return `Max ${v.maxLength} characters`
      break
    }
  }
  return null
}

export function FormPreview({
  fields, workspaceName, title, description,
  showSubmitterFields = false, onMockSubmit, success, successMessage,
  onFileUpload, fileUploadConfig, prefill,
}: FormPreviewProps) {
  const [values, setValues] = useState<Record<string, string>>(
    () => ({ ...defaultValues(fields), ...(prefill ? prefillValues(fields, prefill, validateField) : {}) })
  )
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitterName, setSubmitterName] = useState('')
  const [submitterEmail, setSubmitterEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [step, setStep] = useState(0)
  // Uploaded file metadata per field id drives the preview card while values hold the key.

  // Buttons hold no value, so a form never asks for one
  // Layout elements are shown but hold no value
  const displayFields = fields.filter(f => !isButtonField(f)).sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
  const sortedFields = displayFields.filter(isDataField)
  // "Show when…" rules: hidden fields are not shown and never block the submit
  const answers = valuesByKey(sortedFields, f => values[f.id])
  // Fields the designer hides still take part (prefilled from the link) but are not shown
  const shownFields = displayFields.filter(f => isFieldVisible(f, answers) && !isHiddenOnPublicForm(f))

  // Page breaks split the form into steps; hiding fields can make a step disappear
  const steps = splitIntoSteps(shownFields)
  const current = Math.min(step, Math.max(steps.length - 1, 0))
  const multiStep = steps.length > 1
  const stepFields = steps[current]?.fields ?? []

  /** Errors of the submitter details and the given fields, keyed like `errors`. */
  const collectErrors = (toCheck: FormField[], includeSubmitter: boolean) => {
    const found: Record<string, string> = {}
    if (showSubmitterFields && includeSubmitter) {
      if (!submitterName.trim()) found['__name'] = 'Name is required'
      if (!submitterEmail.trim()) found['__email'] = 'Email is required'
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(submitterEmail)) found['__email'] = 'Invalid email format'
    }
    for (const field of toCheck.filter(isDataField)) {
      const err = validateField(values[field.id] ?? '', field)
      if (err) found[field.id] = err
    }
    return found
  }

  const handleNext = () => {
    const found = collectErrors(stepFields, current === 0)
    setErrors(found)
    if (Object.keys(found).length === 0) setStep(current + 1)
  }

  const handleSubmit = () => {
    // Fields hidden on the public form still count (a link may fill them); fields hidden by a rule do not
    const answered = sortedFields.filter(f => isFieldVisible(f, answers))
    const newErrors = collectErrors(answered, true)

    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) {
      // take the person to the first step that needs attention
      const first = steps.findIndex((st, i) => (i === 0 && (newErrors['__name'] || newErrors['__email']))
        || st.fields.some(fld => newErrors[fld.id]))
      if (first >= 0) setStep(first)
      return
    }

    if (onMockSubmit) {
      // An answer typed before a rule hid its field is not sent: the person no longer sees it
      const sent = Object.fromEntries(answered.filter(f => f.id in values).map(f => [f.id, values[f.id]]))
      onMockSubmit(sent, submitterName, submitterEmail)
    } else {
      setSubmitted(true)
    }
  }

  const isSuccess = success || submitted

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="h-16 w-16 rounded-full bg-success-100 flex items-center justify-center mb-4">
          <Check className="h-8 w-8 text-success-600" />
        </div>
        <h3 className="text-xl font-bold text-neutral-900 mb-2">Submitted!</h3>
        <p className="text-neutral-500 text-sm max-w-sm">
          {successMessage || 'Your response has been recorded. Thank you!'}
        </p>
      </div>
    )
  }

  // One input per field type; the caller wraps it in its grid cell and adds the help text
  const renderInput = (field: FormField) => {
            const opts = field.options as Record<string, unknown> | null
            const choices = opts
              ? (((opts.choices ?? opts.values) as Array<string | { value: string; label?: string }> | undefined) ?? [])
                  .map(choice => typeof choice === 'string' ? choice : choice.value)
              : []

            if (field.type === FormFieldType.SELECT) {
              return (
                <SelectFieldInput
                  key={field.id} field={field} choices={choices}
                  value={values[field.id] ?? ''} error={errors[field.id]}
                  onChange={v => { setValues(p => ({ ...p, [field.id]: v })); setErrors(p => ({ ...p, [field.id]: '' })) }}
                />
              )
            }

            if (field.type === FormFieldType.RADIO) {
              return <ChoiceFieldInput key={field.id} field={field} choices={choices} value={values[field.id] ?? ''} error={errors[field.id]} multiple={false} onChange={v => { setValues(p => ({ ...p, [field.id]: v })); setErrors(p => ({ ...p, [field.id]: '' })) }} />
            }

            if (field.type === FormFieldType.CHECKBOX) {
              const mode = String((field.options as Record<string, unknown> | null)?.selectionMode ?? 'MULTIPLE').toUpperCase()
              if (mode === 'SINGLE') {
                return <ChoiceFieldInput key={field.id} field={field} choices={choices} value={values[field.id] ?? ''} error={errors[field.id]} multiple={false} onChange={v => { setValues(p => ({ ...p, [field.id]: v })); setErrors(p => ({ ...p, [field.id]: '' })) }} />
              }
              let selected: string[] = []
              try { selected = JSON.parse(values[field.id] ?? '[]') } catch { /* empty */ }
              return <ChoiceFieldInput key={field.id} field={field} choices={choices} value={selected} error={errors[field.id]} multiple onChange={v => { setValues(p => ({ ...p, [field.id]: v })); setErrors(p => ({ ...p, [field.id]: '' })) }} />
            }

            if (field.type === FormFieldType.TOGGLE) {
              return <BooleanFieldInput key={field.id} field={field} value={values[field.id] ?? ''} error={errors[field.id]} onChange={v => { setValues(p => ({ ...p, [field.id]: v })); setErrors(p => ({ ...p, [field.id]: '' })) }} />
            }

            if (field.type === FormFieldType.PHONE) {
              return <PhoneFieldInput key={field.id} id={field.id} label={field.label} required={field.required} value={values[field.id] ?? ''} error={errors[field.id]} options={field.options} onChange={v => { setValues(p => ({ ...p, [field.id]: v })); setErrors(p => ({ ...p, [field.id]: '' })) }} />
            }

            if (field.type === FormFieldType.PARAGRAPH || field.type === FormFieldType.RATING) {
              const id = `field-${field.id}`
              return (
                <div key={field.id} className="space-y-1">
                  <label htmlFor={id} className="block text-sm font-medium text-neutral-700">
                    {field.label}{field.required && <span className="text-rose-500 ml-0.5">*</span>}
                  </label>
                  <RecordFieldInput
                    field={field} id={id} value={values[field.id] ?? ''} error={errors[field.id] || null}
                    onChange={v => { setValues(p => ({ ...p, [field.id]: v })); setErrors(p => ({ ...p, [field.id]: '' })) }}
                  />
                  {errors[field.id] && (
                    <p id={`${id}-error`} className="flex items-center gap-1 text-xs text-rose-600">
                      <AlertCircle className="h-3 w-3" />{errors[field.id]}
                    </p>
                  )}
                </div>
              )
            }

            if (field.type === FormFieldType.FILE_UPLOAD) {
              const validations = (field.validations ?? {}) as Record<string, unknown>
              return (
                <div key={field.id} className="space-y-1">
                  <label className="block text-sm font-medium text-neutral-700">
                    {field.label}
                    {field.required && <span className="ml-0.5 text-rose-500">*</span>}
                  </label>

                  <FileFieldInput
                    field={field}
                    value={values[field.id] ?? ''}
                    onChange={v => { setValues(p => ({ ...p, [field.id]: v })); setErrors(p => ({ ...p, [field.id]: '' })) }}
                    upload={onFileUpload ? (file, options) => onFileUpload(field, file, options) : undefined}
                    fileConfig={fileUploadConfig}
                    error={errors[field.id]}
                  />

                  {/* Per-field size limits shown as a hint */}
                  {!!(validations.minFileSizeBytes || validations.maxFileSizeBytes) && (
                    <p className="text-[11px] text-neutral-400">
                      {validations.minFileSizeBytes ? `Min ${Math.round(Number(validations.minFileSizeBytes) / (1024 * 1024) * 10) / 10} MB` : ''}
                      {validations.minFileSizeBytes && validations.maxFileSizeBytes ? ' | ' : ''}
                      {validations.maxFileSizeBytes ? `Max ${Math.round(Number(validations.maxFileSizeBytes) / (1024 * 1024) * 10) / 10} MB` : ''}
                    </p>
                  )}
                </div>
              )
            }

            const prefix = field.type === FormFieldType.CURRENCY ? '$' : undefined
            const rangeValidations = (field.validations ?? {}) as Record<string, unknown>
            return (
              <FieldInput
                key={field.id} id={field.id}
                label={field.label} type={getInputType(field.type as FormFieldType)}
                required={field.required} value={values[field.id] ?? ''}
                error={errors[field.id]} prefix={prefix}
                onChange={v => { setValues(p => ({ ...p, [field.id]: v })); setErrors(p => ({ ...p, [field.id]: '' })) }}
                min={rangeValidations.min as number | undefined} max={rangeValidations.max as number | undefined} step={rangeValidations.step as number | undefined}
              />
            )
  }

  return (
    <div className="space-y-5">
      {/* Workspace / form header */}
      {(workspaceName || title || description) && (
        <div className="rounded-xl border border-neutral-200 bg-gradient-to-br from-primary-50 to-white p-5">
          {workspaceName && (
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-500 mb-1">
              {workspaceName}
            </p>
          )}
          {title && <h2 className="text-lg font-bold text-neutral-900">{title}</h2>}
          {description && <p className="text-sm text-neutral-500 mt-1">{description}</p>}
        </div>
      )}

      {/* Submitter info */}
      {showSubmitterFields && current === 0 && (
        <div className="space-y-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4">
          <p className="text-xs font-bold uppercase tracking-widest text-neutral-400">Your info</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldInput
              id="__name" label="Full Name" type="text" required
              value={submitterName} error={errors['__name']}
              onChange={v => { setSubmitterName(v); setErrors(p => ({ ...p, __name: '' })) }}
            />
            <FieldInput
              id="__email" label="Email Address" type="email" required
              value={submitterEmail} error={errors['__email']}
              onChange={v => { setSubmitterEmail(v); setErrors(p => ({ ...p, __email: '' })) }}
            />
          </div>
        </div>
      )}

      {multiStep && (
        <div className="space-y-1.5" aria-label="Progress">
          <p className="text-xs font-semibold text-neutral-500">
            Step {current + 1} of {steps.length}{steps[current].title && ` · ${steps[current].title}`}
          </p>
          <div className="h-1.5 w-full rounded-full bg-neutral-100" role="progressbar" aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={current + 1}>
            <div className="h-1.5 rounded-full bg-primary-500 transition-all" style={{ width: `${((current + 1) / steps.length) * 100}%` }} />
          </div>
        </div>
      )}

      {/* Form fields */}
      <div className="grid grid-cols-1 gap-x-3 gap-y-3 sm:grid-cols-2">
        {displayFields.length === 0 ? (
          <p className="text-center text-sm text-neutral-400 py-8 italic sm:col-span-2">
            No fields added yet — add some in the Design tab.
          </p>
        ) : (
          stepFields.map(field => {
            if (isLayoutField(field)) {
              // A line break is an empty full-width cell, so the next field starts on a new row
              return (
                <div key={field.id} className={cn('sm:col-span-2', field.type !== FormFieldType.LINE_BREAK && 'pt-1')}>
                  <LayoutFieldView field={field} />
                </div>
              )
            }
            const half = fieldWidth(field) === 'half' && field.type !== FormFieldType.PARAGRAPH
            return (
              <div key={field.id} className={cn('min-w-0', !half && 'sm:col-span-2')}>
                {renderInput(field)}
                <FieldHelpText field={field} className="mt-1" />
              </div>
            )
          })
        )}
      </div>

      {/* Submit */}
      <div className="flex gap-3">
        {multiStep && current > 0 && (
          <button
            onClick={() => setStep(current - 1)}
            className="h-11 rounded-xl border border-neutral-200 bg-white px-5 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 transition-colors"
          >
            Back
          </button>
        )}
        <button
          onClick={multiStep && current < steps.length - 1 ? handleNext : handleSubmit}
          className="flex-1 h-11 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-colors shadow-sm"
        >
          {multiStep && current < steps.length - 1 ? 'Next' : 'Submit'}
        </button>
      </div>

    </div>
  )
}


